import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Loader2, X, ClipboardCheck } from "lucide-react";
import { api, fmtDate, STATUS_STYLES } from "@/lib/api";

const field = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-sm text-navy shadow-none focus:border-gold focus:outline-none";
const label = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/60";

const EMPTY = { name: "", audit_type: "Internal", framework: "Internal review", scheduled_date: "", auditor: "", scope: "", requirements: "", evidence_checklist: [], findings: "", severity: "", corrective_actions: "", owner: "", due_date: "", closure_evidence: "", status: "Scheduled", next_review_date: "" };

export default function Audits() {
  const [audits, setAudits] = useState([]);
  const [meta, setMeta] = useState({ frameworks: [] });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [evidenceText, setEvidenceText] = useState("");
  const [params] = useSearchParams();

  const load = () => {
    api.get("/api/app/audits").then((r) => setAudits(r.data));
    api.get("/api/app/audits/meta").then((r) => setMeta(r.data));
  };
  useEffect(load, []);

  useEffect(() => {
    if (params.get("new")) setEditing({ ...EMPTY });
  }, [params]);

  const openEdit = (a) => {
    setEditing({ ...a });
    setEvidenceText((a.evidence_checklist || []).join("\n"));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...editing, evidence_checklist: evidenceText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing.audit_id) await api.put(`/api/app/audits/${editing.audit_id}`, payload);
      else await api.post("/api/app/audits", payload);
      setEditing(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="audits-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Audit Management</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Audit Register</h1>
          <p className="mt-2 max-w-xl text-sm text-ink/60">
            Internal reviews, customer audits, regulatory inspections, and third-party assessments — with evidence, findings,
            and closure tracking.
          </p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="audit-add-button" className="btn-gold !py-2.5">
          <Plus className="h-4 w-4" /> Log an Audit
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white" data-testid="audits-table">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
              {["ID", "Audit name", "Type", "Framework", "Scheduled", "Owner", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {audits.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-ink/50">No audits recorded yet. Log your first audit — internal or external.</td></tr>
            )}
            {audits.map((a) => (
              <tr key={a.audit_id} className="border-b border-navy/5 hover:bg-paper/60" data-testid={`audit-row-${a.audit_id}`}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{a.audit_id}</td>
                <td className="max-w-xs px-4 py-3 font-medium text-navy">{a.name}</td>
                <td className="px-4 py-3 text-ink/60">{a.audit_type}</td>
                <td className="px-4 py-3 text-ink/60">{a.framework}</td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(a.scheduled_date)}</td>
                <td className="px-4 py-3 text-ink/60">{a.owner || a.auditor || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[a.status] || "bg-navy/5 text-navy/60 border-navy/10"}`}>{a.status}</span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => openEdit(a)} data-testid={`audit-edit-${a.audit_id}`} className="text-sm font-semibold text-golddark hover:text-navy">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-navy/50" onClick={() => setEditing(null)} data-testid="audit-drawer-backdrop">
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-white p-8" data-testid="audit-drawer">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold text-navy">{editing.audit_id ? `Edit ${editing.audit_id}` : "Log an Audit"}</h2>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close" data-testid="audit-drawer-close"><X className="h-5 w-5 text-navy/50" /></button>
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={label}>Audit name *</label>
                <input required className={field} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="audit-input-name" />
              </div>
              <div>
                <label className={label}>Internal or external *</label>
                <select className={field} value={editing.audit_type} onChange={(e) => setEditing({ ...editing, audit_type: e.target.value })} data-testid="audit-input-type">
                  <option>Internal</option><option>External</option>
                </select>
              </div>
              <div>
                <label className={label}>Industry / framework *</label>
                <select className={field} value={editing.framework} onChange={(e) => setEditing({ ...editing, framework: e.target.value })} data-testid="audit-input-framework" required>
                  {(meta.frameworks || ["Internal review"]).map((fr) => <option key={fr}>{fr}</option>)}
                  {!meta.frameworks?.includes(editing.framework) && editing.framework ? <option>{editing.framework}</option> : null}
                </select>
              </div>
              <div>
                <label className={label}>Scheduled date</label>
                <input type="date" className={field} value={editing.scheduled_date || ""} onChange={(e) => setEditing({ ...editing, scheduled_date: e.target.value })} data-testid="audit-input-scheduled" />
              </div>
              <div>
                <label className={label}>Auditor / responsible party</label>
                <input className={field} value={editing.auditor} onChange={(e) => setEditing({ ...editing, auditor: e.target.value })} data-testid="audit-input-auditor" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Audit scope</label>
                <textarea className={`${field} min-h-16`} value={editing.scope} onChange={(e) => setEditing({ ...editing, scope: e.target.value })} data-testid="audit-input-scope" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Requirements being evaluated</label>
                <textarea className={`${field} min-h-16`} value={editing.requirements} onChange={(e) => setEditing({ ...editing, requirements: e.target.value })} data-testid="audit-input-requirements" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Evidence checklist (one per line)</label>
                <textarea className={`${field} min-h-20`} value={evidenceText} onChange={(e) => setEvidenceText(e.target.value)} data-testid="audit-input-evidence" placeholder={"Current COI\nLicense copy"} />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Findings</label>
                <textarea className={`${field} min-h-16`} value={editing.findings} onChange={(e) => setEditing({ ...editing, findings: e.target.value })} data-testid="audit-input-findings" />
              </div>
              <div>
                <label className={label}>Severity</label>
                <select className={field} value={editing.severity} onChange={(e) => setEditing({ ...editing, severity: e.target.value })} data-testid="audit-input-severity">
                  <option value="">—</option>{["Critical", "High", "Medium", "Low"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Owner</label>
                <input className={field} value={editing.owner} onChange={(e) => setEditing({ ...editing, owner: e.target.value })} data-testid="audit-input-owner" />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Corrective actions</label>
                <textarea className={`${field} min-h-16`} value={editing.corrective_actions} onChange={(e) => setEditing({ ...editing, corrective_actions: e.target.value })} data-testid="audit-input-corrective" />
              </div>
              <div>
                <label className={label}>Due date</label>
                <input type="date" className={field} value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} data-testid="audit-input-due" />
              </div>
              <div>
                <label className={label}>Next review date</label>
                <input type="date" className={field} value={editing.next_review_date || ""} onChange={(e) => setEditing({ ...editing, next_review_date: e.target.value })} data-testid="audit-input-next-review" />
              </div>
              <div>
                <label className={label}>Closure evidence</label>
                <input className={field} value={editing.closure_evidence} onChange={(e) => setEditing({ ...editing, closure_evidence: e.target.value })} data-testid="audit-input-closure" />
              </div>
              <div>
                <label className={label}>Status</label>
                <select className={field} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="audit-input-status">
                  {["Scheduled", "In Progress", "Completed", "Closed"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button type="submit" disabled={busy} data-testid="audit-save-button" className="btn-gold flex-1 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                Save Audit
              </button>
              {editing.audit_id && (
                <button type="button" onClick={async () => { await api.delete(`/api/app/audits/${editing.audit_id}`); setEditing(null); load(); }} data-testid="audit-delete-button" className="rounded-sm border border-red-200 px-5 text-sm font-semibold text-red-600 hover:bg-red-50">
                  Delete
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
